import React, { useEffect, useState } from 'react';
/* calculationHelpers is untyped JS (allowJs) — shared with the single-product measurement module. */
import { GRADING_INPUT_PATTERN, isValidGradingInput, validateIntermediateGradingInput } from '../../../../../../../utils/calculationHelpers';
import { isSizeRendered } from '../size-ranges';
import type { BombiixCellRendererParams } from '../../../../../../../Components/BombiixAGGrid/types';
import type { MeasurementGridRow } from '../grid-types';

/** cellProps passed per size column so the cell knows which grading size it edits. */
interface SizeCellProps extends BombiixCellRendererParams<MeasurementGridRow> {
  sizeName?: string;
}

const toDisplay = (value: unknown): string => (value === null || value === undefined ? '' : String(value));

/** A grading with no stored value anywhere is a zero delta — show it as 0 rather than as a blank box. */
const toGradingDisplay = (value: unknown): string => {
  const text = toDisplay(value);
  return text === '' ? '0' : text;
};

/** Accept only in-progress / valid grading input (mirrors CardDragSpec's onChange gate). */
const isAcceptableInput = (value: string): boolean => validateIntermediateGradingInput(value) && (value === '' || isValidGradingInput(value));

/**
 * Editable size cell (BM-9210). The `measurment` input is editable ONLY on the anchor (sample/zero)
 * size; every other size's measurment is derived by the calc engine and shown read-only. The stacked
 * `grading` input (shown when show-grading is on) is editable ONLY on non-anchor sizes. The cell turns
 * amber when the size was changed (`isUserChanged`). Commits on blur via `onRowChange` with an encoded
 * field `measurment:<size>` / `grading:<size>` that the grid parses into a reducer action.
 */
const SizeCell: React.FC<SizeCellProps> = ({ data, sizeName, onRowChange }) => {
  const grading = (data?.gradings ?? []).find((entry) => entry.sizeName === sizeName);
  const rowId = data?.id ?? '';
  const [measurment, setMeasurment] = useState(toDisplay(grading?.measurment));
  const [gradingValue, setGradingValue] = useState(toGradingDisplay(grading?.grading));

  /* Re-sync the editable inputs whenever the underlying value changes externally (Push, Clear-highlights,
     recalculation, or AG-Grid reusing this cell for another row). Deps only change on commit/blur, so this
     never fights active typing. Without this the local state stays stale after a Push (BM-9210 fix). */
  useEffect(() => {
    setMeasurment(toDisplay(grading?.measurment));
    setGradingValue(toGradingDisplay(grading?.grading));
  }, [rowId, grading?.measurment, grading?.grading]);

  /* Size cells belong to rows that carry a POM item; rows past the POM count leave the spec columns blank. */
  if (!data?.hasPom) return null;

  /* The size columns are the union across the whole cohort, so a column routinely stands for a size THIS
     product does not show — either because it hides it or because the size lies outside its own selected
     size range. Both render blank, which is the only thing distinguishing two products under one grading
     group. The grading is left untouched behind the blank: the size keeps calculating and every size after
     it still grades through it. A size no product shows never reaches here — it is dropped from the column
     set instead. */
  if (sizeName && !isSizeRendered(data.productSizeRanges ?? [], data.hiddenSizeNames ?? [], sizeName)) return null;

  const hasIsZeroKey = grading ? Object.prototype.hasOwnProperty.call(grading, 'isZero') : false;
  const hasIsSampleKey = grading ? Object.prototype.hasOwnProperty.call(grading, 'isSampleSize') : false;
  /* Editable only on the anchor: a non-anchor cell (key present but flag false) is a derived output. */
  const measurmentDisabled = (hasIsZeroKey && !grading?.isZero) || (hasIsSampleKey && !grading?.isSampleSize);
  const gradingDisabled = Boolean(grading?.isZero || grading?.isSampleSize);

  /* Blur fires on every focus, so skip the write when nothing actually changed — otherwise merely
     tabbing through a cell whose grading displays the implicit 0 would persist that 0 over a stored
     blank and mark the row dirty. */
  const commit = (field: 'measurment' | 'grading', value: string): void => {
    const stored = field === 'grading' ? toGradingDisplay(grading?.grading) : toDisplay(grading?.measurment);
    if (value === stored) return;
    onRowChange?.(rowId, `${field}:${sizeName ?? ''}`, value);
  };

  const restrictKeyPress = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!GRADING_INPUT_PATTERN.test(event.key)) event.preventDefault();
  };

  return (
    <div className={`bulk-measure-size-cell${grading?.isUserChanged ? ' bg-amber' : ''}`}>
      <input
        type="text"
        className="form-control input-spec-text input-border text-center"
        value={measurmentDisabled ? toDisplay(grading?.measurment) : measurment}
        disabled={measurmentDisabled}
        onKeyPress={restrictKeyPress}
        onChange={(event) => {
          if (isAcceptableInput(event.target.value)) setMeasurment(event.target.value);
        }}
        onBlur={() => commit('measurment', measurment)}
      />
      {data?.activeGrading && (
        <input
          type="text"
          className="form-control input-spec-text input-border text-center mt-1"
          value={gradingDisabled ? toGradingDisplay(grading?.grading) : gradingValue}
          disabled={gradingDisabled}
          onKeyPress={restrictKeyPress}
          onChange={(event) => {
            if (isAcceptableInput(event.target.value)) setGradingValue(event.target.value);
          }}
          onBlur={() => commit('grading', gradingValue)}
        />
      )}
    </div>
  );
};

export default SizeCell;
